/**
 * Шифрование сообщений — AES-256-GCM на WebCrypto.
 *
 * Почему не XChaCha20 из libsodium, которым шифруется всё остальное: этот код
 * выполняется в Service Worker при каждом входящем пуше. WebCrypto доступен
 * там сразу и нативно, а libsodium пришлось бы каждый раз поднимать из WASM —
 * лишние сотни миллисекунд на пробуждении, где бюджет и так жёсткий,
 * особенно на iOS. Ключ чата приходит сюда уже распечатанным: сам обмен
 * ключами делает libsodium в основном потоке (см. crypto.ts).
 */
import { decodeUtf8, encodeUtf8, fromBase64, toBase64 } from './bytes.js';

const IV_BYTES = 12; // рекомендованная длина IV для GCM

export interface Envelope {
  nonce: string;
  ciphertext: string;
}

export async function importChatKey(raw: Uint8Array): Promise<CryptoKey> {
  return crypto.subtle.importKey('raw', raw as BufferSource, 'AES-GCM', false, [
    'encrypt',
    'decrypt',
  ]);
}

/**
 * Связанные данные привязывают шифротекст к чату, отправителю и эпохе ключа.
 *
 * Все три значения приходят из конверта сервера, так что подмена любого из них
 * ломает расшифровку. Сервер не может ни переставить сообщение в другой чат,
 * ни приписать его другому человеку — получатель это заметит.
 */
export function associatedData(chatId: string, senderId: string, keyEpoch: number): Uint8Array {
  return encodeUtf8(`mess/v1|${chatId}|${senderId}|${keyEpoch}`);
}

export async function encryptMessage(
  key: CryptoKey,
  plaintext: string,
  ad: Uint8Array,
): Promise<Envelope> {
  const iv = crypto.getRandomValues(new Uint8Array(IV_BYTES));
  const ciphertext = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv: iv as BufferSource, additionalData: ad as BufferSource },
    key,
    encodeUtf8(plaintext) as BufferSource,
  );

  return { nonce: toBase64(iv), ciphertext: toBase64(new Uint8Array(ciphertext)) };
}

/** Возвращает null, если расшифровать не удалось: не тот ключ, не та эпоха или подмена. */
export async function decryptMessage(
  key: CryptoKey,
  envelope: Envelope,
  ad: Uint8Array,
): Promise<string | null> {
  try {
    const plaintext = await crypto.subtle.decrypt(
      {
        name: 'AES-GCM',
        iv: fromBase64(envelope.nonce) as BufferSource,
        additionalData: ad as BufferSource,
      },
      key,
      fromBase64(envelope.ciphertext) as BufferSource,
    );
    return decodeUtf8(new Uint8Array(plaintext));
  } catch {
    return null;
  }
}
